import { useEffect, useState } from "react";
import { Outlet, useMatch, useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Row, useBreakpoints, Tabs } from "@narmi/design_system";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import { PAYMENT_ACTIVITY_ROUTE } from "./routes";
import PageLayout from "../../src/components/PageLayout";

type PaymentActivityContainerProps = {
  showIndividualPaymentsTab?: boolean;
  showFileImportTab?: boolean;
};

const PaymentActivityContainer = ({
  showIndividualPaymentsTab = true,
  showFileImportTab = false,
}: PaymentActivityContainerProps) => {
  // localization & responsive layout
  const { l10n } = useLocalization();
  const { m } = useBreakpoints();

  // feature flags, routing & navigation
  const features = useUserFeatures();
  const navigate = useNavigate();
  const bulkPaymentsEnabled = features?.bulk_payments;
  const isBulk =
    bulkPaymentsEnabled &&
    Boolean(useMatch(`/${PAYMENT_ACTIVITY_ROUTE}/bulk/*`));
  const isFileImport =
    showFileImportTab &&
    Boolean(useMatch(`/${PAYMENT_ACTIVITY_ROUTE}/file-import/*`));

  // tabs render conditionally, so an index only means something relative to the ones on screen
  const visibleTabs = [
    ...(showIndividualPaymentsTab ? (["individual"] as const) : []),
    ...(bulkPaymentsEnabled ? (["bulk"] as const) : []),
    ...(showFileImportTab ? (["fileImport"] as const) : []),
  ];

  const tabRoutes = {
    individual: `/${PAYMENT_ACTIVITY_ROUTE}`,
    bulk: `/${PAYMENT_ACTIVITY_ROUTE}/bulk`,
    fileImport: `/${PAYMENT_ACTIVITY_ROUTE}/file-import`,
  };

  // UI state
  const getSelectedTab = () => {
    if (isFileImport) return Math.max(visibleTabs.indexOf("fileImport"), 0);
    if (isBulk) return Math.max(visibleTabs.indexOf("bulk"), 0);
    return Math.max(visibleTabs.indexOf("individual"), 0);
  };

  const [selectedTab, setSelectedTab] = useState(getSelectedTab());

  useEffect(() => {
    setSelectedTab(getSelectedTab());
  }, [isBulk, isFileImport]);

  const handleTabChange = (tabIndex: number) => {
    const tab = visibleTabs[tabIndex];
    navigate(tab ? tabRoutes[tab] : `/${PAYMENT_ACTIVITY_ROUTE}`);
  };

  return (
    <PageLayout>
      <div className="margin--top--xxl margin--bottom--xl">
        {m ? (
          <Row>
            <Row.Item>
              <h1>{l10n.getString("payment-activity-title")}</h1>
            </Row.Item>
          </Row>
        ) : (
          <>
            <h1 className="margin--bottom--m">
              {l10n.getString("payment-activity-title")}
            </h1>
          </>
        )}
      </div>
      <Tabs onTabChange={handleTabChange} selectedIndex={selectedTab}>
        <Tabs.List>
          {showIndividualPaymentsTab ? (
            <Tabs.Tab
              label={l10n.getString("individual-payments-tab")}
              tabId="individual-payment"
            />
          ) : null}
          {bulkPaymentsEnabled ? (
            <Tabs.Tab
              label={l10n.getString("bulk-payments-tab")}
              tabId="bulk-payment"
            />
          ) : null}
          {showFileImportTab ? (
            <Tabs.Tab
              label={l10n.getString("file-import-tab")}
              tabId="file-import"
            />
          ) : null}
        </Tabs.List>
      </Tabs>
      <div>
        <Outlet />
      </div>
    </PageLayout>
  );
};

export default PaymentActivityContainer;
